import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Icon } from '@chakra-ui/react';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { MdAdd } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import MiniCalendar from 'components/calendar/MiniCalendar';
import DateSidebar from './DateSidebar';
import ClassDetailsModal from 'views/admin/calendar/components/InstructorClassDetailsModal';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import {
  buildAssignedInstructorClassesQuery,
  filterInstructorVisibleClasses,
  sortAssignedClassesByDate,
} from 'utils/instructorClassQueries';

const db = getFirestore(app);

export default function InstructorDashboardCalendarView() {
  const { userDocId } = useContext(AuthContext);
  const history = useHistory();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [facilitiesById, setFacilitiesById] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userDocId) {
      setClasses([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const assignedQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const nextClasses = snapshot.docs.map((classDoc) => ({
          id: classDoc.id,
          ...classDoc.data(),
        }));
        setClasses(sortAssignedClassesByDate(filterInstructorVisibleClasses(nextClasses)));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading instructor dashboard calendar:', error);
        setLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userDocId]);

  const monthClasses = useMemo(() => {
    const monthStart = new Date(activeMonth.getFullYear(), activeMonth.getMonth(), 1).getTime();
    const monthEnd = new Date(activeMonth.getFullYear(), activeMonth.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
    return classes.filter((classData) => classData.Date >= monthStart && classData.Date <= monthEnd);
  }, [classes, activeMonth]);

  useEffect(() => {
    let isActive = true;
    const missingFacilityIds = Array.from(new Set(
      monthClasses
        .map((classData) => classData.FacilityID)
        .filter((facilityId) => facilityId && !facilitiesById[facilityId])
    ));

    if (missingFacilityIds.length === 0) return undefined;

    const loadFacilities = async () => {
      const entries = await Promise.all(missingFacilityIds.map(async (facilityId) => {
        const facilityDoc = await getDoc(doc(db, 'Users', facilityId));
        return [facilityId, facilityDoc?.exists?.() ? facilityDoc.data() : null];
      }));

      if (!isActive) return;
      setFacilitiesById((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    };

    loadFacilities();

    return () => {
      isActive = false;
    };
  }, [monthClasses, facilitiesById]);

  const enrichedMonthClasses = useMemo(() => (
    monthClasses.map((classData) => {
      const facility = facilitiesById[classData.FacilityID] || {};
      return {
        ...classData,
        facilityName: facility.FacilityName || facility.FirstName || classData.FacilityName,
        facilityAddress: facility.Address || classData.Address,
      };
    })
  ), [monthClasses, facilitiesById]);

  const handleMonthChange = (newMonth) => {
    setActiveMonth(newMonth);
  };

  const handleClassDetails = (classItem) => {
    setSelectedClass(classItem);
  };

  const getClassesForDate = (date) => {
    if (!date) return [];
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
    return enrichedMonthClasses.filter((classData) => classData.Date >= dayStart && classData.Date <= dayEnd);
  };

  return (
    <Box>
      <Flex gap="20px" direction={{ base: 'column', lg: 'row' }}>
        <Box w={{ base: '100%', lg: '70%' }}>
          <MiniCalendar
            w="100%"
            maxW="100%"
            selectRange={false}
            onDateChange={setSelectedDate}
            onMonthChange={handleMonthChange}
            classes={enrichedMonthClasses}
          />
        </Box>
        <Box w={{ base: '100%', lg: '30%' }}>
          <Flex mb="16px" justify="flex-end">
            <Button
              leftIcon={<Icon as={MdAdd} />}
              colorScheme="brand"
              onClick={() => history.push('/admin/calendar')}
              w="100%"
            >
              View Calendar
            </Button>
          </Flex>
          <DateSidebar
            selectedDate={selectedDate}
            classes={getClassesForDate(selectedDate)}
            loading={loading}
            onClassDetails={handleClassDetails}
            hasMultipleFacilities={true}
          />
        </Box>
      </Flex>

      <ClassDetailsModal
        isOpen={Boolean(selectedClass)}
        onClose={() => setSelectedClass(null)}
        classData={selectedClass}
        facility={selectedClass ? facilitiesById[selectedClass.FacilityID] : null}
      />
    </Box>
  );
}
